// src/rms/pages/MediaKit.js
// ============================================================================
// Public media kit — /advertise
// ============================================================================
// The page a prospect is sent before a call. It leads with qualified reach
// rather than impression volume, because that is what the audience is worth:
// a few hundred verified operators in one trade, on the screen where they run
// the business, beats millions of untargeted impressions.
//
// Deliberately shows the placement specs and IAB sizes up front so a media
// buyer can check their existing creative fits without asking.
// ============================================================================

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { computeAvails, placementSpecs } from "../ads/avails";
import { ensureRateCard } from "../ads/rateCard";
import { VERTICAL_PRESETS } from "../verticals/presets";
import "../styles/rms.css";
import { getLocale } from "../i18n/locale";
import Brandmark from "../components/Brandmark";

const money = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;

function MediaKit() {
  // Renders outside RmsProvider, like Landing.
  const { t } = useTranslation();
  const [avails, setAvails] = useState(null);
  const [rateCard, setRateCard] = useState([]);

  useEffect(() => {
    ensureRateCard().then((rows) => {
      setRateCard(rows);
      computeAvails().then(setAvails);
    });
  }, []);

  const labelOf = VERTICAL_PRESETS.reduce((acc, p) => ({ ...acc, [p.adCategory]: p.label }), {});
  const specs = placementSpecs();

  const rateFor = (cat, placement) =>
    rateCard.find((r) => r.ad_category === cat && r.placement === placement);

  return (
    <div className="rms-root rms-landing">
      <header
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 24px", maxWidth: 1080, margin: "0 auto",
        }}
      >
        <Brandmark style={{ padding: 0 }} />
        <nav style={{ display: "flex", gap: 10 }}>
          <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/">{t("mediaKit.product", "Product")}</Link>
          <a className="rms-btn rms-btn--primary rms-btn--sm" href="mailto:ads@reservezoo.com">
            {t("mediaKit.talkToSales", "Talk to sales")}
          </a>
        </nav>
      </header>

      <section className="rms-hero" style={{ paddingBottom: 32 }}>
        <span className="rms-pill rms-pill--ok" style={{ marginBottom: 16 }}>
          {t("mediaKit.advertiseWithReservezoo", "Advertise with ReserveZoo")}
        </span>
        <h1>{t("mediaKit.reachRentalOperatorsOn", "Reach rental operators on the screen where they run the business.")}</h1>
        <p>
          {t("mediaKit.notAContentSite", "Not a content site. Not a forum. ReserveZoo is the software independent rental businesses use to take bookings, check out equipment and invoice customers — every working day. Your ad sits beside the work, in front of the person who signs the cheque.")}
        </p>
      </section>

      <section className="rms-landing-section" style={{ paddingTop: 0 }}>
        <div className="rms-feature-grid">
          <div className="rms-card rms-card-pad rms-feature">
            <h3>{t("mediaKit.qualifiedNotBroad", "Qualified, not broad")}</h3>
            <p>
              {t("mediaKit.everyAccountTellsUs", "Every account tells us exactly what it rents out when it signs up. Target boat rental operators and you reach boat rental operators — not people who once read about boats.")}
            </p>
          </div>
          <div className="rms-card rms-card-pad rms-feature">
            <h3>{t("mediaKit.measuredOnViewability", "Measured on viewability")}</h3>
            <p>
              We only count an impression once 50% of the unit has been on screen for a full second —
              the MRC standard. You are billed for what was actually seen.
            </p>
          </div>
          <div className="rms-card rms-card-pad rms-feature">
            <h3>{t("mediaKit.standardIabCreative", "Standard IAB creative")}</h3>
            <p>
              728×90 and 300×250. Your existing banners run unmodified — image, HTML tag or a native
              unit we build to match the interface.
            </p>
          </div>
          <div className="rms-card rms-card-pad rms-feature">
            <h3>{t("mediaKit.neverMidTransaction", "Never mid-transaction")}</h3>
            <p>
              {t("mediaKit.weNeverServeDuring", "We never serve during checkout, payment, signature capture or a damage inspection. It protects the operator's attention, which is what makes the rest of the inventory work.")}
            </p>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="rms-landing-section" style={{ background: "var(--rms-subtle)", maxWidth: "none" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <h2 className="rms-page-title" style={{ textAlign: "center", marginBottom: 8 }}>
            {t("mediaKit.theAudienceByTrade", "The audience, by trade")}
          </h2>
          <p className="rms-page-sub" style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 28px" }}>
            {t("mediaKit.buyOneVerticalOr", "Buy one vertical or several. Every figure below is measured from live accounts, not modelled.")}
          </p>

          {!avails ? (
            <p style={{ textAlign: "center", color: "var(--rms-muted-strong)" }}>Loading audience data…</p>
          ) : avails.rows.length === 0 ? (
            <div className="rms-card rms-card-pad" style={{ textAlign: "center" }}>
              <p style={{ margin: 0, color: "var(--rms-muted-strong)" }}>
                {t("mediaKit.weReOnboardingOur", "We're onboarding our first operators now. Get in touch to reserve a vertical before it opens.")}
              </p>
            </div>
          ) : (
            <div className="rms-table-wrap">
              <table className="rms-table">
                <thead>
                  <tr>
                    <th>{t("mediaKit.vertical", "Vertical")}</th>
                    <th className="rms-num">{t("mediaKit.operators", "Operators")}</th>
                    <th className="rms-num">{t("mediaKit.viewableImpressionsMo", "Viewable impressions / mo")}</th>
                    <th className="rms-num">{t("mediaKit.railCpm", "Rail CPM")}</th>
                    <th className="rms-num">{t("mediaKit.headerCpm", "Header CPM")}</th>
                    <th className="rms-num">{t("availability.from", "From")}</th>
                  </tr>
                </thead>
                <tbody>
                  {avails.rows.map((r) => (
                    <tr key={r.adCategory}>
                      <td><strong>{labelOf[r.adCategory] || r.adCategory}</strong></td>
                      <td className="rms-num">{r.operators}</td>
                      <td className="rms-num">{r.monthlyViewable.toLocaleString(getLocale())}</td>
                      <td className="rms-num">{money(rateFor(r.adCategory, "sidebar")?.rack_cpm_cents)}</td>
                      <td className="rms-num">{money(rateFor(r.adCategory, "banner_top")?.rack_cpm_cents)}</td>
                      <td className="rms-num">{money(rateFor(r.adCategory, "sidebar")?.min_spend_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Placements */}
      <section className="rms-landing-section">
        <h2 className="rms-page-title" style={{ textAlign: "center", marginBottom: 8 }}>
          {t("campaignForm.placements", "Placements")}
        </h2>
        <p className="rms-page-sub" style={{ textAlign: "center", maxWidth: 560, margin: "0 auto 28px" }}>
          {t("mediaKit.passiveDisplayOnlyNo", "Passive display only — no interstitials, no overlays, nothing that interrupts.")}
        </p>
        <div className="rms-feature-grid">
          {specs.map((s) => (
            <div key={s.key} className="rms-card rms-card-pad rms-feature">
              <h3>{s.label}</h3>
              <p>
                <strong>{s.size}</strong> · rotates every {s.rotationSeconds}s
                <br />
                {s.placement === "banner_top"
                  ? "In the header, above the fold on every screen."
                  : "Right rail, in view while the operator works."}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="rms-landing-section" style={{ textAlign: "center" }}>
        <h2 className="rms-page-title" style={{ marginBottom: 10 }}>
          {t("mediaKit.reserveAVertical", "Reserve a vertical")}
        </h2>
        <p className="rms-page-sub" style={{ marginBottom: 22, maxWidth: 560, margin: "0 auto 22px" }}>
          {t("mediaKit.weRunASmall", "We run a small number of advertisers per trade so each one is actually noticed. Tell us who you want to reach and we'll send current availability.")}
        </p>
        <a className="rms-btn rms-btn--primary" href="mailto:ads@reservezoo.com" style={{ padding: "11px 22px" }}>
          ads@reservezoo.com
        </a>
      </section>

      <footer style={{ borderTop: "1px solid var(--rms-border)", padding: "22px 24px", textAlign: "center", fontSize: 12.5, color: "var(--rms-muted)" }}>
        {t("landing.footer", "ReserveZoo — rental management for every trade.")}
      </footer>
    </div>
  );
}

export default MediaKit;
